import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '@/context/LanguageContext';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon with appointment count
const createCustomIcon = (count, isActive) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        position: relative;
        width: 40px;
        height: 50px;
      ">
        <div style="
          background: ${isActive ? 'linear-gradient(135deg, #FF3B30, #FF6B5B)' : 'linear-gradient(135deg, #6B7280, #9CA3AF)'};
          color: white;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border: 2px solid white;
        ">
          <span style="
            transform: rotate(45deg);
            font-weight: bold;
            font-size: 12px;
          ">${count}</span>
        </div>
      </div>
    `,
    iconSize: [40, 50],
    iconAnchor: [20, 50],
    popupAnchor: [0, -40]
  });
};

// Generate random appointments with variation
const generateRandomSlots = (base, variance = 8) => {
  return Math.max(5, base + Math.floor(Math.random() * variance * 2) - variance);
};

// Base location data
const baseLocations = [
  { id: 'tunis', name: 'Tunis', country: 'Tunisia', center: 'TLS', coords: [36.8065, 10.1815], baseSlots: 20, flag: '🇹🇳' },
  { id: 'rabat', name: 'Rabat', country: 'Morocco', center: 'TLS', coords: [34.0209, -6.8416], baseSlots: 20, flag: '🇲🇦' },
  { id: 'casablanca', name: 'Casablanca', country: 'Morocco', center: 'VFS Global', coords: [33.5731, -7.5898], baseSlots: 17, flag: '🇲🇦' },
  { id: 'cairo', name: 'Cairo', country: 'Egypt', center: 'TLS', coords: [30.0444, 31.2357], baseSlots: 18, flag: '🇪🇬' },
  { id: 'alexandria', name: 'Alexandria', country: 'Egypt', center: 'VFS Global', coords: [31.2001, 29.9187], baseSlots: 10, flag: '🇪🇬' },
  { id: 'london', name: 'London', country: 'UK', center: 'VFS Global', coords: [51.5074, -0.1278], baseSlots: 35, flag: '🇬🇧' },
  { id: 'manchester', name: 'Manchester', country: 'UK', center: 'TLS', coords: [53.4808, -2.2426], baseSlots: 20, flag: '🇬🇧' },
  { id: 'dublin', name: 'Dublin', country: 'Ireland', center: 'VFS Global', coords: [53.3498, -6.2603], baseSlots: 25, flag: '🇮🇪' },
  { id: 'algiers', name: 'Algiers', country: 'Algeria', center: 'TLS', coords: [36.7538, 3.0588], baseSlots: 25, flag: '🇩🇿' },
  { id: 'oran', name: 'Oran', country: 'Algeria', center: 'BLS', coords: [35.6969, -0.6331], baseSlots: 12, flag: '🇩🇿' },
  { id: 'lagos', name: 'Lagos', country: 'Nigeria', center: 'VFS Global', coords: [6.5244, 3.3792], baseSlots: 28, flag: '🇳🇬' },
  { id: 'abuja', name: 'Abuja', country: 'Nigeria', center: 'VFS Global', coords: [9.0765, 7.3986], baseSlots: 15, flag: '🇳🇬' },
  { id: 'madrid', name: 'Madrid', country: 'Spain', center: 'BLS', coords: [40.4168, -3.7038], baseSlots: 30, flag: '🇪🇸' },
  { id: 'rome', name: 'Rome', country: 'Italy', center: 'VFS Global', coords: [41.9028, 12.4964], baseSlots: 28, flag: '🇮🇹' },
  { id: 'brussels', name: 'Brussels', country: 'Belgium', center: 'TLS', coords: [50.8503, 4.3517], baseSlots: 16, flag: '🇧🇪' },
  { id: 'lisbon', name: 'Lisbon', country: 'Portugal', center: 'VFS Global', coords: [38.7223, -9.1393], baseSlots: 22, flag: '🇵🇹' },
  { id: 'valletta', name: 'Valletta', country: 'Malta', center: 'VFS Global', coords: [35.8989, 14.5146], baseSlots: 8, flag: '🇲🇹' },
  { id: 'amsterdam', name: 'Amsterdam', country: 'Netherlands', center: 'VFS Global', coords: [52.3676, 4.9041], baseSlots: 19, flag: '🇳🇱' },
  { id: 'budapest', name: 'Budapest', country: 'Hungary', center: 'VFS Global', coords: [47.4979, 19.0402], baseSlots: 15, flag: '🇭🇺' },
  { id: 'reykjavik', name: 'Reykjavik', country: 'Iceland', center: 'VFS Global', coords: [64.1466, -21.9426], baseSlots: 10, flag: '🇮🇸' },
];

// Country stats with base values
const countryStatsBase = [
  { country: 'Tunisia', flag: '🇹🇳', baseSlots: 20, centers: 'TLS' },
  { country: 'Morocco', flag: '🇲🇦', baseSlots: 37, centers: 'TLS, VFS Global' },
  { country: 'Egypt', flag: '🇪🇬', baseSlots: 28, centers: 'TLS, VFS Global' },
  { country: 'UK', flag: '🇬🇧', baseSlots: 55, centers: 'VFS Global, TLS' },
  { country: 'Ireland', flag: '🇮🇪', baseSlots: 25, centers: 'VFS Global' },
  { country: 'Algeria', flag: '🇩🇿', baseSlots: 37, centers: 'TLS, BLS' },
  { country: 'Nigeria', flag: '🇳🇬', baseSlots: 43, centers: 'VFS Global' },
  { country: 'Spain', flag: '🇪🇸', baseSlots: 30, centers: 'BLS' },
  { country: 'Italy', flag: '🇮🇹', baseSlots: 28, centers: 'VFS Global' },
  { country: 'Netherlands', flag: '🇳🇱', baseSlots: 19, centers: 'VFS Global' },
  { country: 'Portugal', flag: '🇵🇹', baseSlots: 22, centers: 'VFS Global' },
  { country: 'Belgium', flag: '🇧🇪', baseSlots: 16, centers: 'TLS' },
  { country: 'Malta', flag: '🇲🇹', baseSlots: 8, centers: 'VFS Global' },
  { country: 'Hungary', flag: '🇭🇺', baseSlots: 15, centers: 'VFS Global' },
  { country: 'Iceland', flag: '🇮🇸', baseSlots: 10, centers: 'VFS Global' },
];

const AppointmentMap = ({ isSubscribed = false }) => {
  const { language } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Center map to show Europe and Africa
  const mapCenter = [35, 10];
  const mapZoom = 3;

  // Truly random number generator
  const getTrueRandom = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Generate dynamic mock data with TRUE randomness
  const generateDynamicData = () => {
    // Update locations with truly random slot counts (variance around base)
    const updatedLocations = baseLocations.map((loc) => ({
      ...loc,
      appointments: getTrueRandom(
        Math.floor(loc.baseSlots * 0.4), 
        Math.floor(loc.baseSlots * 1.6)
      )
    }));
    setLocations(updatedLocations);

    // Update country stats with truly random values
    const updatedStats = countryStatsBase.map((stat) => ({
      ...stat,
      slots: getTrueRandom(
        Math.floor(stat.baseSlots * 0.5), 
        Math.floor(stat.baseSlots * 1.5)
      )
    }));
    setCountryStats(updatedStats);
    
    setLastUpdate(new Date());
  };

  // Initial load and periodic updates (every 30 minutes)
  useEffect(() => {
    generateDynamicData();
    
    // Update every 30 minutes (1800000 ms)
    const interval = setInterval(() => {
      generateDynamicData();
    }, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const translations = {
    en: {
      title: 'Live Visa Appointment Tracking',
      subtitle: 'Track real-time visa appointment availability across TLS Contact, VFS Global, and BLS centers worldwide',
      availableToday: 'appointments available',
      subscribeToSee: 'Subscribe to see details',
      clickToBook: 'Book Now',
      country: 'Country',
      lastUpdated: 'Last updated',
      justNow: 'Just now'
    },
    fr: {
      title: 'Suivi des rendez-vous Visa en direct',
      subtitle: 'Suivez en temps réel la disponibilité des rendez-vous visa dans les centres TLS Contact, VFS Global et BLS',
      availableToday: 'rendez-vous disponibles',
      subscribeToSee: 'Abonnez-vous pour voir',
      clickToBook: 'Réserver',
      country: 'Pays',
      lastUpdated: 'Dernière mise à jour',
      justNow: 'À l\'instant'
    }
  };

  const t = translations[language] || translations.en;

  return (
    <div className="w-full" data-testid="appointment-map-section">
      {/* Marketing Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium mb-3">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          LIVE TRACKING
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">
          {t.title}
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
          {t.subtitle}
        </p>
      </div>

      {/* Map Container */}
      <div className="relative rounded-lg overflow-hidden shadow-xl border border-gray-200">
        <MapContainer 
          center={mapCenter} 
          zoom={mapZoom} 
          scrollWheelZoom={true}
          style={{ height: '450px', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {locations.map((location) => (
            <Marker 
              key={location.id}
              position={location.coords}
              icon={createCustomIcon(location.appointments, isSubscribed)}
            >
              <Popup>
                <div className="text-center min-w-[180px] p-2">
                  <div className="text-2xl mb-1">{location.flag}</div>
                  <h3 className="font-bold text-lg text-[#0A0A0A]">{location.name}</h3>
                  <p className="text-gray-500 text-sm">{location.country}</p>
                  <p className="text-xs text-blue-600 font-medium">{location.center}</p>
                  
                  {isSubscribed ? (
                    <>
                      <div className="my-3 py-2 bg-green-50 rounded">
                        <span className="text-2xl font-bold text-green-600">{location.appointments}</span>
                        <p className="text-xs text-green-700">{t.availableToday}</p>
                      </div>
                      <a 
                        href={location.center === 'TLS' 
                          ? "https://visas-fr.tlscontact.com/" 
                          : location.center === 'VFS Global'
                          ? "https://www.vfsglobal.com/"
                          : "https://www.blsinternational.com/"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-[#FF3B30] text-white text-sm px-4 py-2 rounded hover:bg-[#E5352B] transition-colors"
                      >
                        {t.clickToBook} →
                      </a>
                    </>
                  ) : (
                    <div className="my-3 py-3 bg-gray-100 rounded">
                      <p className="text-xs text-gray-500">{t.subscribeToSee}</p>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Legend with last update */}
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 z-[1000]">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#FF3B30] to-[#FF6B5B]"></div>
              <span className="text-gray-700">{isSubscribed ? 'Active' : 'Premium'}</span>
            </div>
            <div className="text-gray-400">|</div>
            <div className="text-gray-600">
              {t.lastUpdated}: {t.justNow}
            </div>
          </div>
        </div>
      </div>

      {/* Country Stats Grid - Dynamic */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 gap-3">
        {countryStats.map((stat) => (
          <div 
            key={stat.country}
            className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow hover:border-[#FF3B30]/30"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{stat.flag}</span>
              <span className="font-medium text-sm text-[#0A0A0A] truncate">{stat.country}</span>
            </div>
            {isSubscribed ? (
              <>
                <p className="text-xs text-gray-500">
                  <span className="font-bold text-[#FF3B30] text-lg">{stat.slots}</span> slots
                </p>
                <p className="text-xs text-blue-500 truncate">{stat.centers}</p>
              </>
            ) : (
              <p className="text-xs text-gray-400">Subscribe to view</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppointmentMap;
